import { useEffect, useMemo, useState, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDealsForSelectedArea } from '../services/apiService';
import WalkerBottomNav from '../components/WalkerBottomNav';
import Logo from '../components/Logo';
import { DEAL_CATEGORIES, normalizeCategory } from '../constants/categories';
import { isDealLiveNow } from '../services/dealAvailability';
import './FeedPage.css';

// Filter pills: "Alle", "Nu actief", then exactly the categories a retailer
// can pick when creating a deal (src/constants/categories.js). Matching is on
// the deal's category only — the old free-text matching on title/description
// ("korting", "lunch", …) is what put a Tech deal under "Eten & Drinken".
const FILTERS = [
  { id: 'all', label: 'Alle', match: () => true },
  { id: 'live', label: 'Nu actief', match: (deal) => isDealLiveNow(deal) },
  ...DEAL_CATEGORIES.map((c) => ({
    id: `cat:${c}`,
    label: c,
    category: c,
    match: (deal) => normalizeCategory(deal.category) === c,
  })),
];

function readSession() {
  const lat = parseFloat(sessionStorage.getItem('walkin_user_lat'));
  const lng = parseFloat(sessionStorage.getItem('walkin_user_lng'));
  const areaName =
    sessionStorage.getItem('walkin_area_name') ||
    sessionStorage.getItem('walkin_shop_area') ||
    '';
  return {
    areaName,
    locationStatus: sessionStorage.getItem('walkin_location_status') || 'unavailable',
    userPos:        isNaN(lat) || isNaN(lng) ? null : { lat, lng },
  };
}

// Returns the expiry label plus whether the deal expires today/soon so the
// card can colour it. Same-day deals collapse to a "Vandaag tot 23:59"
// shorthand instead of spelling out the full date.
function expiryDisplay(iso) {
  if (!iso) return { text: '', soon: false };
  const end = new Date(iso);
  const now = new Date();
  const isToday = end.toDateString() === now.toDateString();
  const hoursLeft = (end.getTime() - now.getTime()) / 3600000;
  const soon = isToday || (hoursLeft > 0 && hoursLeft <= 24);
  if (isToday) return { text: 'Vandaag tot 23:59', soon: true };
  const dd = String(end.getDate()).padStart(2, '0');
  const mm = String(end.getMonth() + 1).padStart(2, '0');
  const yyyy = end.getFullYear();
  return { text: `Geldig t/m ${dd}-${mm}-${yyyy} 23:59`, soon };
}

function timeRemainingText(iso) {
  if (!iso) return null;
  const end = new Date(iso).getTime();
  const now = Date.now();
  if (end <= now) return { text: 'Verlopen', type: 'expired' };
  const diffMin = Math.floor((end - now) / 60000);
  const h = Math.floor(diffMin / 60);
  const m = diffMin % 60;
  // Beyond two days "Nog 479u 59m" is unreadable; count in days instead.
  if (h >= 48) return { text: `Nog ${Math.floor(h / 24)} dagen`, type: 'active' };
  return {
    text: h > 0 ? `Nog ${h}u ${m}m` : `Nog ${m}m`,
    type: diffMin <= 120 ? 'urgent' : 'active',
  };
}

export default function FeedPage() {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeShop, setActiveShop] = useState('all');
  const [tick, forceUpdate] = useReducer((x) => x + 1, 0);

  const { areaName, locationStatus } = readSession();

  useEffect(() => {
    let cancelled = false;

    // Deals come from the backend only. There is deliberately no local or
    // seeded fallback: when the API has nothing for this location the feed
    // shows its empty state rather than inventing placeholder content.
    const loadDeals = async () => {
      // Scoped to the walker's gebied: /deals/nearby cannot filter by area, so
      // getDealsForSelectedArea queries from the area centroid with a radius
      // that cannot reach a neighbouring gebied. Without this, every Amsterdam
      // area showed every Amsterdam deal.
      try {
        const apiDeals = await getDealsForSelectedArea();
        if (!cancelled) setDeals(Array.isArray(apiDeals) ? apiDeals : []);
      } catch {
        if (!cancelled) setDeals([]);
      }
    };

    loadDeals();
    window.addEventListener('focus', loadDeals);
    window.addEventListener('walkin-deals-change', loadDeals);
    const id = setInterval(forceUpdate, 60000);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', loadDeals);
      window.removeEventListener('walkin-deals-change', loadDeals);
      clearInterval(id);
    };
  }, [areaName]);

  const dotClass =
    locationStatus === 'approximate'
      ? 'feed__location-dot--approximate'
      : locationStatus === 'active'
        ? 'feed__location-dot--active'
        : 'feed__location-dot--unavailable';

  const locationText = areaName
    ? `${areaName} · ${deals.length} ${deals.length === 1 ? 'deal' : 'deals'}`
    : 'Locatie onbekend';

  const debugMode =
    typeof window !== 'undefined' && window.location.search.includes('debug=1');

  const showJourneyBanner = deals.length >= 3;
  const journeyStops = deals.slice(0, 5);

  const availableShops = Array.from(
    new Set(deals.map((d) => d.shopName).filter(Boolean))
  ).sort();

  // Priority: shop > category. When a shop is picked we show ALL of that
  // shop's deals regardless of category — the category filter is preserved
  // in state but visually overridden until the shop filter is cleared.
  const filteredDeals = useMemo(() => {
    if (activeShop !== 'all') {
      return deals.filter((d) => d.shopName === activeShop);
    }
    if (activeCategory === 'all') return deals;
    const category = FILTERS.find((c) => c.id === activeCategory);
    if (!category) return deals;
    return deals.filter((d) => category.match(d));
    // `tick` re-runs this every minute so "Nu actief" follows the clock.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deals, activeCategory, activeShop, tick]);

  const liveCount = useMemo(
    () => deals.filter((d) => isDealLiveNow(d)).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deals, tick]
  );
  const countFor = (f) => (f.id === 'all' ? deals.length : deals.filter(f.match).length);

  const hasActiveFilter = activeCategory !== 'all' || activeShop !== 'all';
  const clearFilters = () => {
    setActiveCategory('all');
    setActiveShop('all');
  };

  return (
    <div className="feed">
      {debugMode && (
        <div className="feed__debug-banner">
          <div>📍 Gebied: <strong>{areaName}</strong></div>
          <div>🏪 Shop deals (matching): <strong>{deals.length}</strong></div>
          <div>🔑 Source: API /deals/nearby (areaId)</div>
        </div>
      )}

      <header
        className="feed__header"
        style={{ paddingTop: 'max(env(safe-area-inset-top), 44px)' }}
      >
        <div className="feed__header-top">
          <div className="feed__header-left">
            <button
              className="feed__back"
              onClick={() => navigate('/')}
              aria-label="Terug naar start"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 5l-7 7 7 7" />
              </svg>
            </button>
            <Logo size={24} />
          </div>
          <button className="feed__bell" aria-label="Notificaties">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
          </button>
        </div>

        <div className="feed__location">
          <span className={`feed__location-dot ${dotClass}`} />
          <span className="feed__location-text">{locationText}</span>
          <button
            className="feed__switch-area"
            onClick={() => navigate('/detect')}
          >
            Wissel gebied
          </button>
        </div>

        <div className="feed__bar">
          <span className="feed__bar-label">Deals in jouw buurt</span>
          <button
            type="button"
            className="feed__bar-badge"
            onClick={() => { setActiveShop('all'); setActiveCategory('live'); }}
          >
            {liveCount} nu actief
          </button>
        </div>
      </header>

      {deals.length > 0 && (
        <>
          <div
            className={
              'feed__filter-bar' + (activeShop !== 'all' ? ' shop-active' : '')
            }
          >
            {FILTERS.map((f) => {
              const n = countFor(f);
              return (
                <button
                  key={f.id}
                  className={
                    `feed__filter-pill${activeCategory === f.id ? ' active' : ''}` +
                    (n === 0 && f.id !== 'all' ? ' feed__filter-pill--empty' : '')
                  }
                  onClick={() => setActiveCategory(f.id)}
                >
                  {f.label}
                  <span className="feed__filter-count-badge">{n}</span>
                </button>
              );
            })}
          </div>

          {activeShop !== 'all' && activeCategory !== 'all' && (
            <div className="feed__filter-notice">
              <span>Winkelfilter actief — toont alle deals van {activeShop}</span>
              <button onClick={() => setActiveShop('all')}>× Wis winkelfilter</button>
            </div>
          )}

          <div className="feed__shop-filter">
            <select
              className="feed__shop-select"
              value={activeShop}
              onChange={(e) => setActiveShop(e.target.value)}
            >
              <option value="all">
                Alle winkels ({availableShops.length})
              </option>
              {availableShops.map((shop) => (
                <option key={shop} value={shop}>{shop}</option>
              ))}
            </select>
          </div>

          {activeShop !== 'all' ? (
            <div className="feed__filter-count">
              {filteredDeals.length} deal{filteredDeals.length !== 1 ? 's' : ''} van {activeShop}
            </div>
          ) : activeCategory !== 'all' ? (
            <div className="feed__filter-count">
              {activeCategory === 'live'
                ? `${filteredDeals.length} deal${filteredDeals.length !== 1 ? 's' : ''} nu te gebruiken`
                : `${filteredDeals.length} deal${filteredDeals.length !== 1 ? 's' : ''} in ${FILTERS.find((c) => c.id === activeCategory)?.label}`}
            </div>
          ) : (
            <div className="feed__filter-count">
              {filteredDeals.length} deals in {areaName}
            </div>
          )}

          {hasActiveFilter && (
            <button className="feed__clear-filters" onClick={clearFilters}>
              × Wis alle filters
            </button>
          )}
        </>
      )}

      <main className="feed__main">
        {showJourneyBanner && (
          <button
            type="button"
            className="feed__journey"
            onClick={() => navigate('/journey')}
          >
            <div className="feed__journey-text">
              <p className="feed__journey-eyebrow">Winkelroute</p>
              <h2 className="feed__journey-title">
                {areaName} — {deals.length} stops beschikbaar
              </h2>
            </div>
            <div className="feed__journey-stops">
              {journeyStops.map((_, i, arr) => (
                <span key={i} className="feed__journey-stop">
                  <span className="feed__journey-dot" />
                  {i < arr.length - 1 && <span className="feed__journey-line" />}
                </span>
              ))}
            </div>
          </button>
        )}

        {deals.length === 0 ? (
          <div className="feed__empty">
            <h3 className="feed__empty-title">Nog geen deals in dit gebied</h3>
            <p className="feed__empty-text">Kom later terug.</p>
            <p className="feed__empty-sub">
              Ben je een winkelier? Maak een deal aan en hij verschijnt hier
              direct.
            </p>
            <button
              className="feed__empty-btn"
              onClick={() => navigate('/shop/login')}
            >
              Inloggen als winkelier →
            </button>
          </div>
        ) : filteredDeals.length === 0 ? (
          <div className="feed__filter-empty">
            <p>Geen deals gevonden</p>
            {hasActiveFilter && (
              <button onClick={clearFilters}>Wis alle filters</button>
            )}
          </div>
        ) : (
          <>
            <h3 className="feed__section-title">Dichtstbij jou</h3>
            <div className="feed__cards">
              {filteredDeals.map((d) => {
                const ts = timeRemainingText(d.expiresAt);
                const exp = expiryDisplay(d.expiresAt);
                return (
                  <button
                    key={d.id}
                    className="feed__card"
                    onClick={() => navigate(`/deal/${d.id}`)}
                  >
                    <div className="feed__card-body">
                      <div className="feed__card-shop">{d.shopName}</div>
                      <p className="feed__card-deal">{d.title}</p>
                      <span
                        className={`feed__card-time ${exp.soon ? 'feed__card-time--soon' : 'feed__card-time--muted'}`}
                      >
                        {exp.text}
                      </span>
                    </div>
                    {ts && (
                      <span
                        className={`feed__card-remaining feed__card-remaining--${ts.type}`}
                      >
                        {ts.text}
                        {ts.type === 'urgent' && (
                          <span className="feed__card-urgent">verloopt bijna</span>
                        )}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </main>

      <WalkerBottomNav />
    </div>
  );
}
